import { useCallback, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useCountryCode } from '#src/app/shared/hooks/l10n';
import { isUserPhoneNumberValid } from '#src/app/shared/utils/phoneCollection';
import type { PhoneNumber } from '#src/app/shared/utils/phoneCollection';

import type { PhoneOptInEventHandlers } from '../events';
import type { PhoneOptInResult } from '../types';
import { useConsent } from '../useConsent';
import { useSubmitPhoneOptIn } from '../useSubmitPhoneOptIn';
import type { PhoneOptInPendingAction } from '../useSubmitPhoneOptIn';

type UseJournalistOptInFormParams = Readonly<{
	inputRef: React.RefObject<HTMLInputElement | null>;
	/**
	 * The number currently on the account, including one saved by a preceding screen in the same
	 * session.
	 */
	phoneNumber: PhoneNumber;
	events: PhoneOptInEventHandlers;
	onComplete: (result: PhoneOptInResult) => void;
}>;

export type UseJournalistOptInFormReturn = ModelHookResult<
	{
		hasPhone: boolean;
		phoneNumber: PhoneNumber;
		inputError: string | undefined;
		inputValue: string;
		countryCode: string;
		isSubmitting: boolean;
		/** Which ask is in flight, so the surface spins the control the starter pressed. */
		pendingAction: PhoneOptInPendingAction | null;
	},
	{
		handleOnChange: (value: string | undefined) => void;
		onSubmit: () => Promise<void>;
		onAccept: () => Promise<void>;
		onDecline: () => Promise<void>;
	}
>;

/**
 * Drives the journalist opt-in screen: input/validation state, consent, and submission.
 *
 * Any valid number is accepted.
 */
export function useJournalistOptInForm({
	inputRef,
	phoneNumber,
	events,
	onComplete,
}: UseJournalistOptInFormParams): UseJournalistOptInFormReturn {
	const countryCode = useCountryCode();
	const { translate } = useI18n();

	const consent = useConsent('phone_journalist');
	const { isSubmitting, pendingAction, submit, decline } = useSubmitPhoneOptIn({
		screen: 'journalist',
		consent,
		consentTrackingKey: 'phone_consent_starter_phone_collection',
		events,
		onComplete,
	});

	const hasPhone = useMemo(() => isUserPhoneNumberValid(phoneNumber), [phoneNumber]);

	const [inputValue, setInputValue] = useState('');
	const [inputError, setInputError] = useState<string>();
	const [isInputDirty, setIsInputDirty] = useState(false);

	const handleOnChange = useCallback(
		(value: string | undefined) => {
			setInputError(undefined);
			setInputValue(value ?? '');
			if (!isInputDirty) {
				events.onPhoneEntry('journalist');
				setIsInputDirty(true);
			}
		},
		[isInputDirty, events],
	);

	const onSubmit = useCallback(async () => {
		if (inputValue && !isUserPhoneNumberValid(inputValue)) {
			setInputError(translate('corgi.petition.starter_onboarding.phone_collection_step_journalist.input_error'));
			events.onValidationError('journalist', 'invalid_number');
			inputRef.current?.focus();
			return;
		}

		const addedPhone = !!inputValue && isInputDirty && inputValue !== phoneNumber;

		await submit(inputValue || undefined, phoneNumber, {
			has_phone: !!phoneNumber,
			added_phone: addedPhone,
		});
	}, [inputValue, submit, phoneNumber, translate, isInputDirty, inputRef, events]);

	// has-phone sub-case: the number is already fixed, so accepting only ever saves consent — never a
	// phone-number change — and declining has no number to save at all.
	const onAccept = useCallback(async () => {
		await submit(phoneNumber, phoneNumber, { has_phone: true, added_phone: false, accepted: true });
	}, [submit, phoneNumber]);

	const onDecline = useCallback(async () => {
		await decline(phoneNumber, { has_phone: true, added_phone: false, accepted: false });
	}, [decline, phoneNumber]);

	return {
		data: {
			hasPhone,
			phoneNumber,
			inputError,
			inputValue,
			countryCode,
			isSubmitting,
			pendingAction,
		},
		actions: {
			handleOnChange,
			onSubmit,
			onAccept,
			onDecline,
		},
	};
}
